// What a signer does, the same for the customer (from their emailed link) and the countersigner (in courageMFG):
// agree to sign electronically, read the contract with their fields highlighted where they sit on the PDF, fill
// them in (a typed or drawn signature, name, title; the date fills itself), then Sign. Or Decline, with a reason.
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { Ink, SigField, SignBody, SignerView } from "./sgApi";
import { ROLE_NAME, shortSha } from "./sgApi";
import "./signing.css";

type Props = {
  view: SignerView;
  /** The PDF bytes behind a document URL (public: plain fetch; in courageMFG: signed in). */
  pdf: (url: string) => Promise<Blob>;
  /** Open or download a document. */
  openDoc: (url: string, name: string, download?: boolean) => void;
  consent: () => Promise<void>;
  sign: (body: SignBody) => Promise<SignerView>;
  decline?: (reason: string) => Promise<SignerView>;
  onChange: (v: SignerView) => void;
  /** Where "Verify a copy" goes. */
  verifyHref: string;
  /** Extra actions under a finished state (back to the contract). */
  after?: ReactNode;
};

const todayText = () => new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
const when = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "");
const day = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "");
const FIELD_NAME: Record<string, string> = { signature: "Signature", initials: "Initials", name: "Name", title: "Title", date: "Date", text: "Text", checkbox: "Check" };
const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

export function SignDocument({ view, pdf, openDoc, consent, sign, decline, onChange, verifyHref, after }: Props) {
  const r = view.request;
  const mine = useMemo(() => view.fields.filter((f) => f.mine).sort((a, b) => a.page - b.page || b.y - a.y || a.x - b.x), [view.fields]);
  const [agreed, setAgreed] = useState(!!view.recipient.consentAt);
  const [ticked, setTicked] = useState(false);
  const [vals, setVals] = useState<Record<string, string | boolean>>(() => Object.fromEntries(mine.filter((f) => f.type === "name" || f.type === "title").map((f) => [f.id, (f.type === "name" ? view.recipient.name : view.recipient.title) ?? ""])));
  const [ink, setInk] = useState<Ink | null>(null);
  const [sheet, setSheet] = useState<string | null>(null); // the field the sheet opened from, or "" for all
  const [confirming, setConfirming] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const filled = (f: SigField) => f.type === "signature" || f.type === "initials" ? !!ink : f.type === "date" ? true : f.type === "checkbox" ? vals[f.id] === true : !!String(vals[f.id] ?? "").trim();
  const required = mine.filter((f) => f.required);
  const left = required.filter((f) => !filled(f));
  const ready = view.status === "ready";

  const next = () => {
    const f = left[0];
    if (!f) return;
    document.getElementById(`sgf-${f.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    setSheet(f.id);
  };
  const doConsent = async () => {
    setBusy(true); setErr(null);
    try { await consent(); setAgreed(true); window.scrollTo({ top: 0 }); } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  };
  const doSign = async () => {
    if (!ink) return;
    setBusy(true); setErr(null);
    try {
      const values: Record<string, string | boolean> = {};
      for (const f of mine) if (f.type !== "signature" && f.type !== "initials" && f.type !== "date") values[f.id] = vals[f.id] ?? "";
      const out = await sign({ consent: true, ink, values, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone });
      setConfirming(false); onChange(out); window.scrollTo({ top: 0 });
    } catch (e) { setErr(errText(e)); setConfirming(false); } finally { setBusy(false); }
  };
  const doDecline = async (reason: string) => {
    if (!decline) return;
    setBusy(true); setErr(null);
    try { onChange(await decline(reason)); setDeclining(false); window.scrollTo({ top: 0 }); } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  };

  const intro = (
    <div className="sg-card">
      <h1>{r.title}</h1>
      <p><strong>{r.senderName}</strong> at {r.companyName} sent you contract {r.number}{r.version > 1 ? ` (revision ${r.version})` : ""} to review and sign.</p>
      {r.message && <div className="sg-msg">{r.message}</div>}
      <div className="sg-steps" aria-label="Who signs">
        {view.signers.map((s, i) => (
          <span key={i} className={s.status === "signed" ? "done" : s.name === view.recipient.name && s.role === view.recipient.role ? "now" : ""}>
            {i + 1}. {s.name} ({ROLE_NAME[s.role].toLowerCase()}){s.status === "signed" ? ", signed" : ""}
          </span>
        ))}
      </div>
    </div>
  );

  // ----- Finished or closed: say where it stands and give the documents -----
  if (!ready) {
    const docLink = (url: string | undefined, name: string, label: string) => url && (
      <><button className="btn" onClick={() => openDoc(url, name)}>{label}</button><button className="btn" onClick={() => openDoc(url, name, true)}>Download</button></>
    );
    const turn = view.signers.find((s) => s.status !== "signed");
    const card =
      view.status === "completed" ? (
        <div className="sg-card ok" role="status">
          <h2>Everyone has signed</h2>
          <p>{view.signers.map((s) => `${s.name} signed ${day(s.signedAt)}`).join(". ")}. Your copy of the signed contract and its certificate of completion are here.</p>
          <div className="sg-row">{docLink(view.documents.signed, `${r.number} signed.pdf`, "Open the Signed Contract")}</div>
          <div className="sg-row">{docLink(view.documents.certificate, `${r.number} certificate.pdf`, "Open the Certificate")}</div>
          {view.fingerprints.signed && <p className="sg-auto">Fingerprint of the signed copy: <span className="mono">{shortSha(view.fingerprints.signed)}</span>. Anyone can check a copy at <a href={verifyHref}>{verifyHref.replace(/^https?:\/\//, "")}</a>.</p>}
        </div>
      ) : view.status === "signed" ? (
        <div className="sg-card ok" role="status">
          <h2>You signed {day(view.recipient.signedAt)}</h2>
          <p>{turn ? <>{turn.name} signs next. {view.recipient.kind === "external" ? "We'll email you the signed contract when everyone has signed." : "The contract is complete when they sign."}</> : "Finishing the signed copy now."}</p>
          <div className="sg-row">{docLink(view.documents.original, `${r.number}.pdf`, "Open the Contract as Sent")}</div>
        </div>
      ) : view.status === "finishing" ? (
        <div className="sg-card" role="status"><h2>Everyone has signed</h2><p>The signed copy is being finished. Reload this page in a moment to get it.</p><div className="sg-row"><button className="btn" onClick={() => location.reload()}>Reload</button></div></div>
      ) : view.status === "waiting" ? (
        <div className="sg-card" role="status"><h2>Not your turn yet</h2><p>{turn?.name ?? "Someone"} signs first. {view.recipient.kind === "internal" ? "You'll get an email when it's your turn." : "We'll email you when it's your turn."}</p><div className="sg-row">{docLink(view.documents.original, `${r.number}.pdf`, "Open the Contract")}</div></div>
      ) : view.status === "copy" ? (
        <div className="sg-card" role="status"><h2>You're copied on this contract</h2><p>You don't need to sign. You'll get the signed copy when everyone has signed.</p><div className="sg-row">{docLink(view.documents.original, `${r.number}.pdf`, "Open the Contract")}</div></div>
      ) : (
        <div className="sg-card warn" role="status">
          <h2>{view.status === "declined" ? "This contract was declined" : view.status === "voided" ? `${r.senderName} cancelled this request` : "This request has expired"}</h2>
          {r.endReason && <p>{view.status === "expired" ? r.endReason : <>Reason: {r.endReason}</>}</p>}
          <p>It can't be signed. {view.status === "declined" ? `${r.companyName} will send a new version if needed.` : `Contact ${r.senderName} at ${r.companyName} if you have questions.`}</p>
        </div>
      );
    return <>{intro}{card}{after}</>;
  }

  // ----- First: agree to sign electronically -----
  if (!agreed) return (
    <>
      {intro}
      <div className="sg-card sg-consent">
        <h2>Agree to sign electronically</h2>
        <ul>{view.consent.map((c, i) => <li key={i}>{c}</li>)}</ul>
        <label className="sg-check"><input type="checkbox" checked={ticked} onChange={(e) => setTicked(e.target.checked)} /> I agree to use electronic records and signatures.</label>
        {err && <div className="msg error" role="alert" style={{ marginTop: 12 }}>{err}</div>}
        <div className="sg-row">
          <button className="btn primary lg" disabled={!ticked || busy} onClick={doConsent}>Continue</button>
          <button className="btn" onClick={() => openDoc(view.documents.original, `${r.number}.pdf`)}>Open the Contract (PDF)</button>
          {decline && <button className="btn" onClick={() => setDeclining(true)}>Decline to Sign</button>}
        </div>
      </div>
      {declining && <DeclineSheet busy={busy} err={err} onClose={() => setDeclining(false)} onDecline={doDecline} />}
    </>
  );

  // ----- The document, with my fields where they are on the PDF -----
  return (
    <>
      {intro}
      {err && <div className="msg error" role="alert">{err}</div>}
      <PdfView url={view.documents.original} pdf={pdf} fields={view.fields} onOpen={() => openDoc(view.documents.original, `${r.number}.pdf`)}
        renderField={(f, scale) => f.mine ? (
          <button id={`sgf-${f.id}`} className={`sg-f mine ${filled(f) ? "filled" : ""}`} onClick={() => setSheet(f.id)} aria-label={`${FIELD_NAME[f.type]}${filled(f) ? ", filled in" : ", needs filling in"}`}>
            <span className="sg-tag">{f.type === "signature" ? "Sign here" : f.label || FIELD_NAME[f.type]}</span>
            <FieldValue f={f} ink={ink} vals={vals} scale={scale} />
          </button>
        ) : (
          <div className="sg-f other" aria-hidden="true"><span className="sg-tag">{ROLE_NAME[f.role]} {f.type === "signature" ? "signs" : FIELD_NAME[f.type].toLowerCase()}</span></div>
        )} />
      <div className="sg-bar" role="region" aria-label="Signing">
        <div className="sg-bar-in">
          <div className="sg-prog">
            {left.length ? <><strong>{left.length}</strong> of {required.length} {required.length === 1 ? "field" : "fields"} left</> : <strong>Ready to sign</strong>}
            <div className="sg-meter" aria-hidden="true"><i style={{ width: `${((required.length - left.length) / Math.max(1, required.length)) * 100}%` }} /></div>
          </div>
          {decline && <button className="btn" onClick={() => setDeclining(true)}>Decline</button>}
          {left.length ? <button className="btn primary lg" onClick={next}>{left.length === required.length ? "Start" : "Next"}</button>
            : <button className="btn primary lg" onClick={() => setConfirming(true)} disabled={busy}>Finish</button>}
        </div>
      </div>
      {sheet !== null && <FillSheet fields={mine} focus={sheet} vals={vals} ink={ink} name={String(vals[mine.find((f) => f.type === "name")?.id ?? ""] ?? view.recipient.name)}
        onClose={() => setSheet(null)} onApply={(v, k) => { setVals(v); setInk(k); setSheet(null); }} />}
      {confirming && (
        <Sheet title="Sign the contract" onClose={() => setConfirming(false)} footer={<><button className="btn" onClick={() => setConfirming(false)}>Back</button><button className="btn primary lg" disabled={busy} onClick={doSign}>{busy ? "Signing…" : "Sign"}</button></>}>
          <div className="sg-typed sg-script" style={{ marginBottom: 12 }}>{ink?.kind === "drawn" ? <img src={ink.image} alt="Your signature" style={{ maxHeight: 64 }} /> : ink?.text}</div>
          <p className="sg-legal">By selecting <strong>Sign</strong>, I agree that this signature is the legal equivalent of my handwritten signature on contract {r.number}{r.version > 1 ? `, revision ${r.version}` : ""}, and that I have read the contract.</p>
        </Sheet>
      )}
      {declining && <DeclineSheet busy={busy} err={err} onClose={() => setDeclining(false)} onDecline={doDecline} />}
    </>
  );
}

function FieldValue({ f, ink, vals, scale }: { f: SigField; ink: Ink | null; vals: Record<string, string | boolean>; scale: number }) {
  const size = Math.max(8, Math.min(f.height * 0.62, 26) * scale);
  if (f.type === "signature" || f.type === "initials") {
    if (!ink) return null;
    return ink.kind === "drawn" ? <img src={ink.image} alt="" /> : <span className="sg-val sg-script" style={{ fontSize: size }}>{ink.text}</span>;
  }
  const v = f.type === "date" ? todayText() : f.type === "checkbox" ? (vals[f.id] === true ? "✓" : "") : String(vals[f.id] ?? "");
  return <span className="sg-val" style={{ fontSize: Math.max(8, 10 * scale) }}>{v}</span>;
}

// ------------------------------------------------------------------------------------------------------------
// The PDF, drawn page by page with pdf.js (loaded only on this page), with fields laid over it
// ------------------------------------------------------------------------------------------------------------

async function pdfjs() {
  const lib = await import("pdfjs-dist/legacy/build/pdf.mjs");
  const worker = (await import("pdfjs-dist/legacy/build/pdf.worker.min.mjs?url")).default;
  lib.GlobalWorkerOptions.workerSrc = worker;
  return lib;
}

export function PdfView({ url, pdf, fields, renderField, onOpen }: {
  url: string; pdf: (url: string) => Promise<Blob>; fields: SigField[]; renderField: (f: SigField, scale: number) => ReactNode; onOpen: () => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [doc, setDoc] = useState<any>(null);
  const [sizes, setSizes] = useState<{ w: number; h: number }[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const canvases = useRef<(HTMLCanvasElement | null)[]>([]);

  useEffect(() => {
    if (!box.current) return;
    const ro = new ResizeObserver(([e]) => setWidth(Math.floor(e.contentRect.width)));
    ro.observe(box.current);
    return () => ro.disconnect();
  }, []);
  useEffect(() => {
    let gone = false;
    (async () => {
      try {
        const [lib, blob] = await Promise.all([pdfjs(), pdf(url)]);
        const d = await lib.getDocument({ data: new Uint8Array(await blob.arrayBuffer()) }).promise;
        const s: { w: number; h: number }[] = [];
        for (let i = 1; i <= d.numPages; i++) { const v = (await d.getPage(i)).getViewport({ scale: 1 }); s.push({ w: v.width, h: v.height }); }
        if (!gone) { setDoc(d); setSizes(s); }
      } catch (e) { if (!gone) setErr(errText(e)); }
    })();
    return () => { gone = true; };
  }, [url]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!doc || !sizes || !width) return;
    const tasks: any[] = [];
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    sizes.forEach(async (s, i) => {
      const canvas = canvases.current[i];
      if (!canvas) return;
      const page = await doc.getPage(i + 1);
      const scale = Math.min(width, 900) / s.w;
      const vp = page.getViewport({ scale: scale * dpr });
      canvas.width = Math.floor(vp.width); canvas.height = Math.floor(vp.height);
      const t = page.render({ canvas, viewport: vp });
      tasks.push(t);
      t.promise.catch(() => { /* cancelled by a resize */ });
    });
    return () => tasks.forEach((t) => t.cancel());
  }, [doc, sizes, width]);

  if (err) return (
    <div className="sg-card warn">
      <h2>The contract can't be shown here</h2>
      <p>Open the PDF to read it, then come back to fill in your fields. ({err})</p>
      <div className="sg-row"><button className="btn" onClick={onOpen}>Open the Contract (PDF)</button></div>
    </div>
  );
  const w = Math.min(width, 900);
  return (
    <div className="sg-doc" ref={box} aria-label="The contract">
      {!sizes ? <div className="sg-card" style={{ width: "100%" }}>Loading the contract…</div> : sizes.map((s, i) => {
        const scale = w / s.w;
        return (
          <div key={i} className="sg-page" style={{ width: w, height: s.h * scale }}>
            <canvas ref={(el) => { canvases.current[i] = el; }} aria-label={`Page ${i + 1} of ${sizes.length}`} role="img" />
            {fields.filter((f) => f.page === i).map((f) => (
              <div key={f.id} style={{ position: "absolute", left: f.x * scale, top: (s.h - f.y - f.height) * scale, width: f.width * scale, height: f.height * scale }}>
                {renderField(f, scale)}
              </div>
            ))}
            <span className="sg-page-n">Page {i + 1} of {sizes.length}</span>
          </div>
        );
      })}
    </div>
  );
}

// ------------------------------------------------------------------------------------------------------------
// Filling in: signature (type or draw), name, title, anything else; the date fills itself
// ------------------------------------------------------------------------------------------------------------

function FillSheet({ fields, focus, vals, ink, name, onClose, onApply }: {
  fields: SigField[]; focus: string; vals: Record<string, string | boolean>; ink: Ink | null; name: string;
  onClose: () => void; onApply: (vals: Record<string, string | boolean>, ink: Ink | null) => void;
}) {
  const [v, setV] = useState(vals);
  const [mode, setMode] = useState<"type" | "draw">(ink?.kind === "drawn" ? "draw" : "type");
  const [typed, setTyped] = useState(ink?.kind === "typed" ? ink.text : name);
  const [drawn, setDrawn] = useState<string | null>(ink?.kind === "drawn" ? ink.image : null);
  const needsInk = fields.some((f) => f.type === "signature" || f.type === "initials");
  const inputs = fields.filter((f) => !["signature", "initials", "date"].includes(f.type));
  const date = fields.find((f) => f.type === "date");
  const nextInk: Ink | null = !needsInk ? null : mode === "draw" ? (drawn ? { kind: "drawn", image: drawn } : null) : typed.trim().length >= 2 ? { kind: "typed", text: typed.trim() } : null;
  const missing = inputs.filter((f) => f.required && (f.type === "checkbox" ? v[f.id] !== true : !String(v[f.id] ?? "").trim()));
  const ok = (!needsInk || nextInk) && !missing.length;
  const focusField = fields.find((f) => f.id === focus);
  return (
    <Sheet title="Fill in and sign" onClose={onClose} footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn primary lg" disabled={!ok} onClick={() => onApply(v, nextInk)}>Apply</button></>}>
      {inputs.map((f) => (
        <div className="field" key={f.id}>
          {f.type === "checkbox" ? (
            <label className="sg-check" style={{ marginTop: 0 }}><input type="checkbox" checked={v[f.id] === true} onChange={(e) => setV({ ...v, [f.id]: e.target.checked })} /> {f.label || "Check"}</label>
          ) : (<>
            <label htmlFor={`fill-${f.id}`}>{f.label || FIELD_NAME[f.type]}{f.required ? "" : " (optional)"}</label>
            <input id={`fill-${f.id}`} className="input" autoFocus={focusField?.id === f.id} value={String(v[f.id] ?? "")} maxLength={f.type === "text" ? 200 : 100}
              autoComplete={f.type === "name" ? "name" : f.type === "title" ? "organization-title" : "off"} onChange={(e) => setV({ ...v, [f.id]: e.target.value })} />
          </>)}
        </div>
      ))}
      {needsInk && (
        <div className="field">
          <span className="label">Signature</span>
          <div className="sg-tabs" role="tablist">
            <button role="tab" aria-selected={mode === "type"} onClick={() => setMode("type")}>Type</button>
            <button role="tab" aria-selected={mode === "draw"} onClick={() => setMode("draw")}>Draw</button>
          </div>
          {mode === "type" ? (<>
            <input className="input" aria-label="Type your signature" value={typed} maxLength={80} onChange={(e) => setTyped(e.target.value)} />
            <div className="sg-typed sg-script" aria-hidden="true" style={{ marginTop: 8 }}>{typed}</div>
          </>) : <SignaturePad value={drawn} onChange={setDrawn} />}
        </div>
      )}
      {date && <p className="sg-auto">Date: {todayText()}, filled in when you sign.</p>}
    </Sheet>
  );
}

/** A signature drawn with a finger, pen or mouse, kept as a PNG cropped to the ink. */
function SignaturePad({ value, onChange }: { value: string | null; onChange: (dataUrl: string | null) => void }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const inked = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);
  useEffect(() => {
    const c = ref.current!;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    c.width = c.clientWidth * dpr; c.height = c.clientHeight * dpr;
    const g = c.getContext("2d")!;
    g.scale(dpr, dpr); g.lineCap = "round"; g.lineJoin = "round"; g.lineWidth = 2.4; g.strokeStyle = "#0D1F59";
    if (value) { const img = new Image(); img.onload = () => g.drawImage(img, 16, (c.clientHeight - Math.min(img.height, c.clientHeight - 20)) / 2, Math.min(img.width, c.clientWidth - 32), Math.min(img.height, c.clientHeight - 20)); img.src = value; inked.current = true; }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const pos = (e: React.PointerEvent) => { const r = ref.current!.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; };
  const down = (e: React.PointerEvent) => { e.preventDefault(); ref.current!.setPointerCapture(e.pointerId); drawing.current = true; last.current = pos(e); };
  const move = (e: React.PointerEvent) => {
    if (!drawing.current) return;
    const g = ref.current!.getContext("2d")!, p = pos(e), l = last.current!;
    g.beginPath(); g.moveTo(l.x, l.y); g.lineTo(p.x, p.y); g.stroke();
    last.current = p; inked.current = true;
  };
  const up = () => { if (!drawing.current) return; drawing.current = false; onChange(inked.current ? cropped(ref.current!) : null); };
  const clear = () => { const c = ref.current!; c.getContext("2d")!.clearRect(0, 0, c.width, c.height); inked.current = false; onChange(null); };
  return (
    <div className="sg-pad">
      <canvas ref={ref} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} aria-label="Draw your signature here" role="img" />
      <span className="sg-pad-line" /><span className="sg-pad-hint">Sign above the line</span>
      <button type="button" className="btn sg-pad-clear" onClick={clear}>Clear</button>
    </div>
  );
}

/** The ink only, with a little margin, at most 600 pixels wide. */
function cropped(c: HTMLCanvasElement): string {
  const g = c.getContext("2d")!;
  const { data, width, height } = g.getImageData(0, 0, c.width, c.height);
  let x0 = width, y0 = height, x1 = -1, y1 = -1;
  for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) if (data[(y * width + x) * 4 + 3] > 16) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
  if (x1 < 0) return c.toDataURL("image/png");
  const pad = 8; x0 = Math.max(0, x0 - pad); y0 = Math.max(0, y0 - pad); x1 = Math.min(width - 1, x1 + pad); y1 = Math.min(height - 1, y1 + pad);
  const w = x1 - x0 + 1, h = y1 - y0 + 1, k = Math.min(1, 600 / w);
  const out = document.createElement("canvas"); out.width = Math.round(w * k); out.height = Math.round(h * k);
  out.getContext("2d")!.drawImage(c, x0, y0, w, h, 0, 0, out.width, out.height);
  return out.toDataURL("image/png");
}

function DeclineSheet({ busy, err, onClose, onDecline }: { busy: boolean; err: string | null; onClose: () => void; onDecline: (reason: string) => void }) {
  const [reason, setReason] = useState("");
  return (
    <Sheet title="Decline to sign" onClose={onClose} footer={<><button className="btn" onClick={onClose}>Back</button><button className="btn primary" disabled={busy || !reason.trim()} onClick={() => onDecline(reason.trim())}>{busy ? "Declining…" : "Decline"}</button></>}>
      <p className="sg-legal">The sender sees your reason and can send a new version. You can't sign this one after declining.</p>
      <div className="field"><label htmlFor="sg-reason">Why are you declining?</label><textarea id="sg-reason" className="textarea" rows={3} maxLength={500} autoFocus value={reason} onChange={(e) => setReason(e.target.value)} /></div>
      {err && <div className="msg error" role="alert">{err}</div>}
    </Sheet>
  );
}

export function Sheet({ title, onClose, footer, children }: { title: string; onClose: () => void; footer?: ReactNode; children: ReactNode }) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", k);
    return () => document.removeEventListener("keydown", k);
  }, [onClose]);
  return (
    <div className="sg-sheet-wrap" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="sg-sheet" role="dialog" aria-modal="true" aria-label={title}>
        <div className="sg-sheet-h"><h2>{title}</h2><button className="sg-x" aria-label="Close" onClick={onClose}>×</button></div>
        <div className="sg-sheet-b">{children}</div>
        {footer && <div className="sg-sheet-f">{footer}</div>}
      </div>
    </div>
  );
}

export { when as signWhen };
